import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Map as MapboxMap, ExpressionSpecification, FilterSpecification, LngLatLike } from 'mapbox-gl';
import { MAPBOX_TOKEN, MissingTokenPlaceholder } from './mapPrimitives';
import {
  CARBON_BAND_COLOR,
  CARBON_BAND_LEGEND,
  UNKNOWN_BAND_COLOR,
  carbonBandForMode,
} from './carbonBands';

/**
 * JourneyRouteMap — an ordered, multi-stop journey drawn on a map.
 *
 * Unlike JourneyMap (a single From → To pair) this renders the full sequence
 * of stops connected in order, with stop markers (start ring / end pin, or
 * numbers on a round trip; see StopMarker) and gently curved arcs
 * between consecutive stops. Owns its own Mapbox setup and route geometry so
 * journey-route presentation can evolve here without touching other maps.
 *
 * The basemap is deliberately minimal (light style, only country names and
 * borders, repainted in a soft warm palette; see styleBasemap). Each segment
 * is colored by the carbon band of its transport mode (carbonBands.ts), flights also dashed and
 * bowed higher so they read without relying on color; a legend explains it.
 *
 * Intentionally decoupled from the app's Journey types: callers map their data
 * down to the minimal {id, lat, lng, label} shape below.
 *
 * Highlight (Journey detail: hovering a stop or a leg in the timeline): what
 * is hovered comes forward, everything else fades back. A leg: its segment
 * thickens and its two stops stay. A stop: only the stop, marked by a map pin
 * that drops onto it, with a bolder name; every line fades well back
 * ('fade'), so after the zoom-in the legs around it don't read as lit (Paulo
 * 2026-10-03). Colors never change (color on this map only means carbon).
 *
 * Interactive stops (Journey detail on desktop, `onStopClick`): hovering a
 * stop on the map shows it as the hovered stop above (pin, the rest fades)
 * with a pointer cursor; a click picks it (the page outlines its card and
 * scrolls to it) and the picked stop keeps its pin (`selectedStopId`); a
 * click on the picked pin is the page's to answer (the stop sheet). The
 * camera never moves for map hovers or clicks: the pin would slide away from
 * under the cursor.
 *
 * The camera follows the `highlight` prop (the timeline) gently: once the cursor settles on an
 * item (FOCUS_DWELL_MS, so running down the list doesn't make the map lurch),
 * it eases a bit closer (ZOOM_IN) to the stop or the leg; once the highlight
 * ends it eases steadily back to the whole journey. Both are measured from
 * the whole-journey framing, never from where the camera happens to be: a
 * return to a snapshot taken mid-way back from the previous hover let the
 * map creep closer each time until it stopped zooming out (Paulo
 * 2026-10-03). Off for prefers-reduced-motion. While the camera is focused,
 * the basemap adds a little context (DETAIL_LABELS): towns and cities nearby,
 * lakes and seas, national parks; gone again at the whole-journey view.
 */

export interface RouteStop {
  id: string | number;
  lat: number;
  lng: number;
  /** Display name, e.g. the city. Shown beneath the numbered marker. */
  label: string;
  /** Transport mode of the leg arriving at this stop (null for the first
   *  stop or when unknown). Colors the segment ending here. */
  arrivalMode?: string | null;
  /** Its number in the journey's sequence (my-trips stopSequenceLabels), so
   *  a round trip's numbered markers match the timeline even when a stop
   *  isn't drawn. Without it, drawn stops are counted in visit order. */
  number?: number | null;
}

/** What the map brings forward, by stop ids (RouteStop.id). */
export type RouteHighlight =
  | { kind: 'stop'; stopId: string | number }
  | { kind: 'leg'; fromStopId: string | number; toStopId: string | number };

/** focus = hovered, normal = connected to it (or nothing hovered), dim = the rest. */
type FocusState = 'focus' | 'normal' | 'dim';
/** Segments add 'fade': every line while a stop is in focus, further back than
 *  'dim' (a hovered leg keeps the rest of the route as context). */
type SegmentState = FocusState | 'fade';

/**
 * A place being considered, not yet in the journey (hovering a suggestion in
 * the stop picker, StopPlacePicker): drawn as a dashed what-if route from the
 * stop before it to the stop after it. Ink, never a carbon color: color on
 * this map only ever means carbon.
 */
export interface RoutePreview {
  lat: number;
  lng: number;
  label: string;
  from?: { lat: number; lng: number } | null;
  to?: { lat: number; lng: number } | null;
}

interface JourneyRouteMapProps {
  stops: RouteStop[];
  /** Bring one stop or leg forward (see the header comment). */
  highlight?: RouteHighlight | null;
  /** Hover preview of a candidate place; null/absent = none. */
  preview?: RoutePreview | null;
  height?: number;
  /** Fill the parent edge to edge, unframed (the desktop side panel of the
   *  detail pages, DetailSplitLayout). `height` is ignored. */
  fill?: boolean;
  /** Makes the stops hoverable and clickable (see the header comment). For
   *  a coordinate visited twice (a round trip's start and end), the first
   *  stop there. */
  onStopClick?: (stopId: string | number) => void;
  /** A click on the map away from any stop (e.g. to clear the pick). */
  onBackgroundClick?: () => void;
  /** The stop picked on the map: it keeps its pin. */
  selectedStopId?: string | number | null;
}

// Stop markers are neutral so color on the map only ever means carbon.
const PIN_COLOR = '#16241d'; // ink: all stop markers
const MULTI_COLOR = '#ffffff'; // hollow dot: more than one stop at this point

const CURVATURE = 0.12;

// Camera on highlight (see the header comment).
const ZOOM_IN = 1.25; // "a bit": about 2.4x closer than the view before
const MAX_FOCUS_ZOOM = 10;
// The whole-journey framing: a single stop's zoom, and the closest a route fits at.
const SINGLE_STOP_ZOOM = 6;
const ROUTE_MAX_ZOOM = 9;
const FOCUS_DWELL_MS = 250;
const RETURN_DWELL_MS = 200;
const FOCUS_DURATION_MS = 900;
const RETURN_DURATION_MS = 1400;
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
const REDUCED_MOTION = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Where the camera goes for a highlight. */
type FocusTarget =
  | { kind: 'point'; coord: [number, number] }
  | { kind: 'line'; coords: Array<[number, number]> };

// Segment styling by highlight state (see the header comment).
const SORT_BY_STATE: ExpressionSpecification = ['match', ['get', 'state'], 'focus', 2, 'normal', 1, 0];
const LINE_WIDTH_BY_STATE: ExpressionSpecification = ['match', ['get', 'state'], 'focus', 6, 3.5];
const LINE_OPACITY_BY_STATE: ExpressionSpecification = ['match', ['get', 'state'], 'dim', 0.3, 'fade', 0.14, 0.95];
const CASING_OPACITY_BY_STATE: ExpressionSpecification = ['match', ['get', 'state'], 'dim', 0.5, 'fade', 0.25, 0.9];

// The focused stop's pin drops onto the coordinate (tip-anchored).
const PIN_DROP_CSS = '@keyframes jrm-pin-drop{from{opacity:0;transform:translateY(-10px) scale(.6)}to{opacity:1;transform:none}}';
// Same teardrop as the journey's end pin (StopMarker 'end'), 18x24 viewBox.
const PIN_PATH = 'M9 1C4.6 1 1 4.5 1 8.9 1 14.6 9 23 9 23s8-8.4 8-14.1C17 4.5 13.4 1 9 1z';
const FLIGHT_CURVATURE = 0.22; // flights arc higher, a cue beyond color

/**
 * Replaces a straight segment with a quadratic Bézier arc bowed off the
 * midpoint. The gentle, consistent curvature reads as a deliberate "route"
 * rather than a raw connect-the-dots polyline.
 */
function curveSegment(
  [x0, y0]: [number, number],
  [x1, y1]: [number, number],
  curvature: number
): Array<[number, number]> {
  const SAMPLES = 24;
  // Control point: segment midpoint pushed perpendicular to the segment.
  const cx = (x0 + x1) / 2 - (y1 - y0) * curvature;
  const cy = (y0 + y1) / 2 + (x1 - x0) * curvature;
  const out: Array<[number, number]> = [];
  for (let t = 0; t <= SAMPLES; t++) {
    const u = t / SAMPLES;
    const inv = 1 - u;
    out.push([
      inv * inv * x0 + 2 * inv * u * cx + u * u * x1,
      inv * inv * y0 + 2 * inv * u * cy + u * u * y1,
    ]);
  }
  return out;
}

// Basemap layers that stay visible: countries (names + national borders) and
// the land/water/landcover fills. Everything else (city, road, POI, transit
// labels, roads, buildings, regional borders) is noise next to the route.
// admin-0-boundary-bg is a wide soft halo under national borders: noise here.
const HIDDEN_LINE_OR_FILL = /^(road|bridge|tunnel|aeroway|building|ferry|admin-1|transit|admin-0-boundary-bg)/;

// Soft, warm palette over light-v11's greys, so the map isn't black-and-white.
// Kept pale and away from the saturated carbon-band colors (carbonBands.ts) so
// the route segments stay the most colorful thing on the map.
const BASEMAP = {
  land: '#f6f1e7',
  landcover: '#dde8d3',
  water: '#c4dde9',
  border: '#cbbfa9',
  countryText: '#a89f90',
};

// Labels light-v11 has that stay hidden until the camera focuses on a stop or
// a leg (Paulo 2026-10-03: "a bit more detail ... major towns and cities
// nearby, ... national parks or lakes"). Muted, in the basemap's warm
// palette, so the route stays the most colorful thing on the map.
const SETTLEMENT_LABELS = ['settlement-major-label', 'settlement-minor-label'];
const WATER_LABELS = ['water-point-label', 'water-line-label'];
const PARK_LABEL = 'poi-label';
const DETAIL_LABELS = [...SETTLEMENT_LABELS, ...WATER_LABELS, PARK_LABEL];
const DETAIL_TEXT = { settlement: '#857b6c', water: '#6d93a7', park: '#6f8766' };
// Only national parks and nature reserves out of the POIs, text only.
const PARK_FILTER: FilterSpecification = ['all', ['==', ['get', 'class'], 'park_like'], ['match', ['get', 'type'], ['National Park', 'Nature Reserve'], true, false]];

// Each map's own filters for the detail layers, so a reveal can narrow them.
const detailBaseFilters = new WeakMap<MapboxMap, Record<string, FilterSpecification | null>>();

/** Show or hide the detail labels. `stopNames`: the route's own stops are
 *  already named by their markers, so the basemap must not name them twice. */
function setDetailLabels(map: MapboxMap, on: boolean, stopNames: string[] = []) {
  let base = detailBaseFilters.get(map);
  if (!base) {
    base = {};
    detailBaseFilters.set(map, base);
  }
  const notAStop: FilterSpecification = ['!', ['in', ['coalesce', ['get', 'name_en'], ['get', 'name']], ['literal', stopNames]]];
  for (const id of DETAIL_LABELS) {
    if (!map.getLayer(id)) continue;
    if (!(id in base)) base[id] = (map.getFilter(id) as FilterSpecification | null | undefined) ?? null;
    if (on) {
      const parts = [
        base[id],
        id === PARK_LABEL ? PARK_FILTER : null,
        SETTLEMENT_LABELS.includes(id) ? notAStop : null,
      ].filter((f): f is FilterSpecification => f != null);
      map.setFilter(id, parts.length === 0 ? null : parts.length === 1 ? parts[0] : (['all', ...parts] as FilterSpecification));
    }
    map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  }
}

function styleBasemap(map: MapboxMap) {
  for (const layer of map.getStyle()?.layers ?? []) {
    const { id, type } = layer;
    const hide = type === 'symbol' ? id !== 'country-label' : HIDDEN_LINE_OR_FILL.test(id);
    if (hide) {
      map.setLayoutProperty(id, 'visibility', 'none');
      continue;
    }
    if (type === 'background') {
      map.setPaintProperty(id, 'background-color', BASEMAP.land);
    } else if (type === 'fill' && id.startsWith('water')) {
      map.setPaintProperty(id, 'fill-color', BASEMAP.water);
    } else if (type === 'line' && id.startsWith('waterway')) {
      map.setPaintProperty(id, 'line-color', BASEMAP.water);
    } else if (type === 'fill' && /^(landcover|national-park|landuse)/.test(id)) {
      map.setPaintProperty(id, 'fill-color', BASEMAP.landcover);
    } else if (type === 'line' && id.startsWith('admin-0-boundary')) {
      map.setPaintProperty(id, 'line-color', BASEMAP.border);
    } else if (id === 'country-label') {
      map.setPaintProperty(id, 'text-color', BASEMAP.countryText);
      map.setPaintProperty(id, 'text-halo-color', BASEMAP.land);
    }
    if (DETAIL_LABELS.includes(id)) {
      const color = SETTLEMENT_LABELS.includes(id) ? DETAIL_TEXT.settlement : WATER_LABELS.includes(id) ? DETAIL_TEXT.water : DETAIL_TEXT.park;
      map.setPaintProperty(id, 'text-color', color);
      map.setPaintProperty(id, 'text-halo-color', BASEMAP.land);
      if (id === PARK_LABEL) map.setLayoutProperty(id, 'icon-image', '');
    }
  }
}

// The legend opens on every load (Paulo 2026-09-30: expanded by default, so
// the night train's "Very low" band is explained without a click); closing
// it lasts the page, not the browser. It remembered a collapse in
// localStorage before (`jody.routeMapLegendOpen`); that key is ignored now.

/** Collapsible legend: the "Carbon intensity" heading toggles the rows. The
 *  open state lives in the map, which reframes the route around it. */
const Legend: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        // Above any click-through overlay a caller lays over the map (e.g.
        // RouteFocus's "open journey details" button) so the toggle works.
        zIndex: 2,
        padding: '5px 8px',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.92)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        columnGap: 6,
        rowGap: 3,
        alignItems: 'center',
        fontSize: 10,
        lineHeight: 1.2,
        color: '#16241d',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={open ? 'Hide carbon intensity legend' : 'Show carbon intensity legend'}
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          padding: 0,
          border: 'none',
          background: 'transparent',
          font: 'inherit',
          fontWeight: 700,
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        Carbon intensity
        <svg
          width={10}
          height={10}
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
        >
          <path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke="#6c7872" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <>
          {CARBON_BAND_LEGEND.map(({ band, label, modes }) => (
            <React.Fragment key={band}>
              <svg width={18} height={6} aria-hidden="true">
                <line
                  x1={2}
                  y1={3}
                  x2={16}
                  y2={3}
                  stroke={CARBON_BAND_COLOR[band]}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeDasharray={band === 'high' ? '4 4' : undefined}
                />
              </svg>
              <span>
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span style={{ color: '#6c7872' }}> · {modes}</span>
              </span>
            </React.Fragment>
          ))}
        </>
      )}
    </div>
  );
};

type MarkerKind = 'start' | 'end' | 'via' | 'numbered' | 'multi';

const HALO = '0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 5px #fff';

/**
 * A stop marker drawn on a zero-size box so the marker's point sits exactly on
 * the coordinate (Marker anchor="center"), with the name floated beneath it.
 *
 * One-way journeys: start = hollow ring, end = solid map pin, stops in between
 * = small dots (direction reads "ring -> pin", like a directions app).
 * Round trips (start == end) number the stops instead, since only the order
 * tells the direction; a coordinate visited more than once is a hollow dot.
 */
const StopMarker: React.FC<{
  kind: MarkerKind;
  label: string;
  number?: number;
  state?: FocusState;
  /** Interactive (onStopClick): a pointer hit area over the coordinate, the pin and the name. */
  onHoverChange?: (hovered: boolean) => void;
  onClick?: () => void;
}> = ({
  kind,
  label,
  number,
  state = 'normal',
  onHoverChange,
  onClick,
}) => {
  const centered: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    boxSizing: 'border-box',
  };
  let glyph: React.ReactNode;
  let labelOffset: number; // px from the coordinate down to the label
  switch (kind) {
    case 'start':
      glyph = (
        <div
          style={{
            ...centered,
            width: 14,
            height: 14,
            background: '#fff',
            border: `3.5px solid ${PIN_COLOR}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
      );
      labelOffset = 9;
      break;
    case 'end':
      // Teardrop pin whose tip sits on the coordinate.
      glyph = (
        <svg
          width={18}
          height={24}
          viewBox="0 0 18 24"
          aria-hidden="true"
          style={{ position: 'absolute', left: -9, top: -23, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))' }}
        >
          <path
            d={PIN_PATH}
            fill={PIN_COLOR}
            stroke="#fff"
            strokeWidth={1.5}
          />
          <circle cx={9} cy={9} r={3} fill="#fff" />
        </svg>
      );
      labelOffset = 3;
      break;
    case 'numbered':
      glyph = (
        <div
          style={{
            ...centered,
            width: 18,
            height: 18,
            background: PIN_COLOR,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
          }}
        >
          {number}
        </div>
      );
      labelOffset = 11;
      break;
    case 'multi':
      glyph = (
        <div
          style={{
            ...centered,
            width: 12,
            height: 12,
            background: MULTI_COLOR,
            border: `2.5px solid ${PIN_COLOR}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }}
        />
      );
      labelOffset = 8;
      break;
    default: // via
      glyph = (
        <div
          style={{
            ...centered,
            width: 9,
            height: 9,
            background: PIN_COLOR,
            border: '1.5px solid #fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }}
        />
      );
      labelOffset = 6;
  }
  const focused = state === 'focus';
  const emphasized = focused || kind === 'start' || kind === 'end';
  const interactive = !!onClick;
  // Only the hit area, the pin and the name take the pointer; the zero-size
  // root stays click-through so the map pans everywhere else.
  const hit: React.CSSProperties = interactive ? { pointerEvents: 'auto', cursor: 'pointer' } : {};

  return (
    <div
      onMouseEnter={interactive ? () => onHoverChange?.(true) : undefined}
      onMouseLeave={interactive ? () => onHoverChange?.(false) : undefined}
      onClick={interactive ? (e) => { e.stopPropagation(); onClick!(); } : undefined}
      style={{
        position: 'relative',
        width: 0,
        height: 0,
        pointerEvents: 'none',
        opacity: state === 'dim' ? 0.4 : 1,
        transition: 'opacity 150ms ease',
      }}
    >
      {interactive && (
        <div
          aria-hidden="true"
          style={{ ...hit, position: 'absolute', left: 0, top: 0, width: 28, height: 28, borderRadius: '50%', transform: 'translate(-50%, -50%)' }}
        />
      )}
      {focused ? (
        // Focused: a map pin in ink (not a color: color on this map only
        // means carbon), its tip on the coordinate over a small ground
        // shadow, dropping in. A round trip's number rides in its head.
        <>
          <div
            aria-hidden="true"
            style={{ position: 'absolute', left: 0, top: 0, width: 14, height: 5, borderRadius: '50%', background: 'rgba(22,36,29,0.3)', filter: 'blur(1px)', transform: 'translate(-50%, -50%)' }}
          />
          <svg
            width={27}
            height={36}
            viewBox="0 0 18 24"
            aria-hidden="true"
            style={{
              ...hit,
              position: 'absolute',
              left: -13.5,
              top: -34.5,
              overflow: 'visible',
              filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))',
              transformOrigin: '50% 100%',
              animation: 'jrm-pin-drop 240ms cubic-bezier(.2,.8,.2,1)',
            }}
          >
            <path d={PIN_PATH} fill={PIN_COLOR} stroke="#fff" strokeWidth={1.2} />
            {number != null
              ? <text x={9} y={11.6} textAnchor="middle" fontSize={7.5} fontWeight={700} fill="#fff">{number}</text>
              : <circle cx={9} cy={9} r={3} fill="#fff" />}
          </svg>
        </>
      ) : glyph}
      <div
        style={{
          ...hit,
          position: 'absolute',
          top: focused ? 5 : labelOffset,
          left: 0,
          transform: 'translateX(-50%)',
          color: emphasized ? '#16241d' : '#3a4a42',
          fontSize: focused ? 12 : emphasized ? 11 : 10,
          fontWeight: emphasized ? 700 : 500,
          whiteSpace: 'nowrap',
          textShadow: HALO,
        }}
      >
        {label}
      </div>
    </div>
  );
};

// The candidate place of a RoutePreview: a hollow ring on the coordinate and
// its name in a small white tag, so it reads as "not in the trip yet".
const PreviewMarker: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ position: 'relative', width: 0, height: 0, pointerEvents: 'none' }}>
    <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)', width: 16, height: 16, borderRadius: '50%', boxSizing: 'border-box', background: '#fff', border: `3px dashed ${PIN_COLOR}`, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
    <div style={{ position: 'absolute', top: 11, left: 0, transform: 'translateX(-50%)', whiteSpace: 'nowrap', background: '#fff', color: PIN_COLOR, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.22)' }}>
      {label}
    </div>
  </div>
);

const validPoint = (p?: { lat: number; lng: number } | null): p is { lat: number; lng: number } =>
  !!p && isFinite(p.lat) && isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);

// The drawn route as JourneyRouteMap builds it (lineGeoJson).
interface SegmentFeature {
  type: 'Feature';
  properties: { color: string; dashed: boolean; fromId: string; toId: string };
  geometry: { type: 'LineString'; coordinates: Array<[number, number]> };
}
interface SegmentCollection {
  type: 'FeatureCollection';
  features: SegmentFeature[];
}

/**
 * Highlight → a SegmentState per segment (as a `state` property the layers
 * style on), a FocusState per marker coordinate, and where the camera would
 * go. Kept apart from lineGeoJson so a hover never refits the map.
 */
function resolveHighlight(
  points: RouteStop[],
  lineGeoJson: SegmentCollection,
  highlight: RouteHighlight | null,
  selectedStopId: string | number | null,
) {
  const coordKeyOf: Record<string, string> = {};
  for (const p of points) coordKeyOf[String(p.id)] = `${p.lng},${p.lat}`;
  // Something not drawn (a stop without coordinates, an id gone after an
  // edit) highlights nothing rather than fading the whole route.
  let active = !!highlight;
  let focusTarget: FocusTarget | null = null;
  const segState: SegmentState[] = lineGeoJson.features.map(() => 'normal');
  const focusKeys = new Set<string>();
  const contextKeys = new Set<string>();
  const ends = (i: number) => [lineGeoJson.features[i].properties.fromId, lineGeoJson.features[i].properties.toId];
  if (highlight?.kind === 'leg') {
    const from = String(highlight.fromStopId);
    const to = String(highlight.toStopId);
    let hit = lineGeoJson.features.findIndex((f) => f.properties.fromId === from && f.properties.toId === to);
    // A stop without coordinates merges two legs into one drawn segment:
    // fall back to the segment that arrives where this leg arrives.
    if (hit < 0) hit = lineGeoJson.features.findIndex((f) => f.properties.toId === to);
    if (hit < 0) active = false;
    else {
      segState.fill('dim');
      segState[hit] = 'focus';
      ends(hit).forEach((id) => contextKeys.add(coordKeyOf[id]));
      focusTarget = { kind: 'line', coords: lineGeoJson.features[hit].geometry.coordinates };
    }
  } else if (highlight?.kind === 'stop') {
    const id = String(highlight.stopId);
    if (!coordKeyOf[id]) active = false;
    else {
      focusKeys.add(coordKeyOf[id]);
      segState.fill('fade');
      const p = points.find((q) => String(q.id) === id)!;
      focusTarget = { kind: 'point', coord: [p.lng, p.lat] };
    }
  }
  const selectedKey = selectedStopId != null ? coordKeyOf[String(selectedStopId)] : undefined;
  const markerState = (lng: number, lat: number): FocusState => {
    const k = `${lng},${lat}`;
    if (k === selectedKey) return 'focus'; // the picked stop keeps its pin
    if (!active) return 'normal';
    if (focusKeys.has(k)) return 'focus';
    return contextKeys.has(k) ? 'normal' : 'dim';
  };
  return {
    styledLines: {
      ...lineGeoJson,
      features: lineGeoJson.features.map((f, i) => ({ ...f, properties: { ...f.properties, state: segState[i] } })),
    },
    markerState,
    highlightActive: active,
    focusTarget,
  };
}

const JourneyRouteMap: React.FC<JourneyRouteMapProps> = ({ stops, highlight = null, preview = null, height = 260, fill = false, onStopClick, onBackgroundClick, selectedStopId = null }) => {
  const mapRef = useRef<MapRef | null>(null);
  // The hover camera has left the whole-journey framing: a way back is due
  // when the highlight ends.
  const cameraAwayRef = useRef(false);

  // Restyle the basemap as soon as the style is parsed ('style.load'), not on
  // the map's 'load' event: that one waits for the first tiles, which would
  // flash the raw grey style (with city labels) for a few seconds first.
  const attachMap = useCallback((ref: MapRef | null) => {
    mapRef.current = ref;
    if (!ref) return;
    const map = ref.getMap();
    if (map.isStyleLoaded()) styleBasemap(map);
    else map.once('style.load', () => styleBasemap(map));
  }, []);

  // Only stops with real coordinates can be placed on the map. A null/0
  // lat or lng means the stop was never geocoded (0,0 is the "null island"
  // sentinel, not a real journey location), so skip it.
  const points = useMemo(
    () =>
      stops.filter(
        (s) => isFinite(s.lat) && isFinite(s.lng) && s.lat !== 0 && s.lng !== 0
      ),
    [stops]
  );

  // One marker per distinct coordinate. A one-way journey marks start/end
  // (ring/pin) and plain dots between; a round trip (first and last stop at
  // the same point) numbers the stops in visit order instead.
  const markers = useMemo(() => {
    const coordKey = (p: RouteStop) => `${p.lng},${p.lat}`;

    // NB: the imported `Map` (react-map-gl) shadows the global Map
    // constructor here, so use a plain object for the per-coordinate tally.
    const counts: Record<string, number> = {};
    for (const p of points) {
      const k = coordKey(p);
      counts[k] = (counts[k] ?? 0) + 1;
    }

    const firstKey = points.length ? coordKey(points[0]) : '';
    const lastKey = points.length ? coordKey(points[points.length - 1]) : '';
    const isRoundTrip = points.length > 2 && firstKey === lastKey;

    const seen = new Set<string>();
    let visit = 0;
    const out: Array<{ id: string | number; lat: number; lng: number; label: string; kind: MarkerKind; number?: number }> = [];

    points.forEach((p) => {
      const k = coordKey(p);
      if (seen.has(k)) return; // already placed a marker at this point
      seen.add(k);

      let kind: MarkerKind;
      let number: number | undefined;
      if (k === firstKey && points.length > 1) kind = 'start';
      else if (!isRoundTrip && k === lastKey) kind = 'end';
      else if ((counts[k] ?? 0) > 1) kind = 'multi';
      else if (isRoundTrip) {
        kind = 'numbered';
        number = p.number ?? ++visit;
      } else kind = 'via';

      out.push({ id: p.id, lat: p.lat, lng: p.lng, label: p.label, kind, number });
    });

    return out;
  }, [points]);

  // One feature per segment, colored by the carbon band of the mode arriving
  // at its end stop. Skipped (ungeocoded) stops just merge into the next segment.
  const lineGeoJson = useMemo(() => {
    const features = points.slice(1).map((to, i) => {
      const from = points[i];
      const band = carbonBandForMode(to.arrivalMode);
      const isFlight = band === 'high';
      return {
        type: 'Feature' as const,
        properties: {
          color: band ? CARBON_BAND_COLOR[band] : UNKNOWN_BAND_COLOR,
          dashed: isFlight,
          fromId: String(from.id),
          toId: String(to.id),
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: curveSegment(
            [from.lng, from.lat],
            [to.lng, to.lat],
            isFlight ? FLIGHT_CURVATURE : CURVATURE
          ),
        },
      };
    });
    return { type: 'FeatureCollection' as const, features };
  }, [points]);

  // A stop hovered on the map itself (interactive stops) shows like a stop
  // hovered in the timeline, without the camera.
  const [mapHoverStopId, setMapHoverStopId] = useState<string | number | null>(null);
  const visualHighlight = useMemo<RouteHighlight | null>(
    () => (mapHoverStopId != null ? { kind: 'stop', stopId: mapHoverStopId } : highlight),
    [mapHoverStopId, highlight],
  );
  const { styledLines, markerState, highlightActive } = useMemo(
    () => resolveHighlight(points, lineGeoJson, visualHighlight, selectedStopId),
    [points, lineGeoJson, visualHighlight, selectedStopId],
  );
  // The camera follows the timeline only (the `highlight` prop).
  const { focusTarget } = useMemo(
    () => resolveHighlight(points, lineGeoJson, highlight, null),
    [points, lineGeoJson, highlight],
  );
  // The what-if route of a hovered candidate place (see RoutePreview). While
  // one shows, it owns the camera: the highlight camera below waits.
  const previewPoint = preview && validPoint(preview) ? preview : null;
  const previewKey = previewPoint ? `${previewPoint.lng},${previewPoint.lat}` : '';


  // The legend only makes sense once at least one segment carries a mode.
  const showLegend = useMemo(
    () => points.slice(1).some((p) => carbonBandForMode(p.arrivalMode) != null),
    [points]
  );
  const [legendOpen, setLegendOpen] = useState(true);
  const toggleLegend = useCallback(() => setLegendOpen((open) => !open), []);

  // The whole drawn route: the arcs, not just the stops, so the margins can
  // stay tight. Null for a single stop.
  const routeBounds = useMemo<[[number, number], [number, number]] | null>(() => {
    if (points.length < 2) return null;
    let minLng = points[0].lng, maxLng = points[0].lng;
    let minLat = points[0].lat, maxLat = points[0].lat;
    for (const f of lineGeoJson.features) {
      for (const [lng, lat] of f.geometry.coordinates) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
    return [[minLng, minLat], [maxLng, maxLat]];
  }, [points, lineGeoJson]);
  // Legend box is ~75px tall open, ~30px collapsed (plus its 8px inset);
  // bottom clears the name under the lowest stop and the Mapbox logo.
  const fitPadding = useMemo(
    () => ({ top: !showLegend ? 32 : legendOpen ? 84 : 44, bottom: 42, left: 36, right: 36 }),
    [showLegend, legendOpen],
  );
  // Frame the whole journey. Called on point changes, when the legend
  // opens/closes (it only reserves room while open), on the map's `load`
  // event (fitBounds before then can run against a not-yet sized canvas and
  // clip the outermost stops), and to come back from a hover (`motion`).
  const fitToPoints = useCallback(
    (animate: boolean, motion?: { duration: number; easing: (t: number) => number }) => {
      const map = mapRef.current;
      if (!map || points.length === 0) return;
      // The fresh framing is the whole journey: no way back pending, and no
      // detail labels at this scale.
      cameraAwayRef.current = false;
      setDetailLabels(map.getMap(), false);
      const duration = animate ? motion?.duration ?? 600 : 0;
      // Only set `easing` when there is one: Mapbox merges the options with
      // Object.assign, so an explicit `easing: undefined` replaces its default
      // and the next frame throws "easing is not a function".
      const ease = motion ? { easing: motion.easing } : {};
      if (!routeBounds) {
        map.flyTo({ center: [points[0].lng, points[0].lat], zoom: SINGLE_STOP_ZOOM, duration, ...ease });
        return;
      }
      map.fitBounds(routeBounds, { padding: fitPadding, duration, ...ease, maxZoom: ROUTE_MAX_ZOOM });
    },
    [points, routeBounds, fitPadding]
  );

  // The zoom of the whole-journey framing: what a hover zooms "a bit closer" from.
  const routeZoom = useCallback((): number | null => {
    const map = mapRef.current?.getMap();
    if (!map) return null;
    if (!routeBounds) return SINGLE_STOP_ZOOM;
    const cam = map.cameraForBounds(routeBounds, { padding: fitPadding, maxZoom: ROUTE_MAX_ZOOM });
    return cam?.zoom ?? null;
  }, [routeBounds, fitPadding]);
  const routeZoomRef = useRef(routeZoom);
  routeZoomRef.current = routeZoom;
  // The route's own stop names, kept out of the detail labels.
  const stopNamesRef = useRef<string[]>([]);
  stopNamesRef.current = points.map((p) => p.label.split(',')[0].trim()).filter(Boolean);

  // Refit when the drawn route changes, not on every new `stops` array: the
  // page polls the journey while prices load, and refitting on identical data
  // would yank the camera back (mid-hover too).
  const routeKey = useMemo(() => points.map((p) => `${p.lng},${p.lat},${p.arrivalMode ?? ''}`).join(';'), [points]);
  const fitRef = useRef(fitToPoints);
  fitRef.current = fitToPoints;
  useEffect(() => {
    fitRef.current(true);
  }, [routeKey, showLegend, legendOpen]);

  // Camera follows the highlight (see the header comment). Keyed on the
  // target's geometry, so a poll that re-renders the same highlight doesn't
  // restart the move.
  const focusKey = focusTarget ? JSON.stringify(focusTarget) : '';
  useEffect(() => {
    const map = mapRef.current?.getMap();
    // A place preview owns the camera (the Replace picker sits inside a
    // hovered stop): no zoom to the stop meanwhile; this picks up again
    // when the preview ends.
    if (!map || REDUCED_MOTION || previewKey) return;
    const target: FocusTarget | null = focusKey ? JSON.parse(focusKey) : null;
    let timer: number | undefined;
    if (target) {
      timer = window.setTimeout(() => {
        const base = routeZoomRef.current() ?? map.getZoom();
        let center: LngLatLike = target.kind === 'point' ? target.coord : target.coords[0];
        let zoom = base + ZOOM_IN;
        if (target.kind === 'line') {
          // Frame the leg with room around it, but only a bit closer than the
          // whole journey: a short leg must not jump to street level.
          const lngs = target.coords.map((c) => c[0]);
          const lats = target.coords.map((c) => c[1]);
          const cam = map.cameraForBounds(
            [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
            { padding: 56 },
          );
          if (cam?.center) center = cam.center;
          if (cam?.zoom != null) zoom = Math.min(cam.zoom, base + ZOOM_IN);
        }
        cameraAwayRef.current = true;
        setDetailLabels(map, true, stopNamesRef.current);
        map.easeTo({ center, zoom: Math.min(zoom, MAX_FOCUS_ZOOM), duration: FOCUS_DURATION_MS, easing: easeInOutCubic });
      }, FOCUS_DWELL_MS);
    } else if (cameraAwayRef.current) {
      // Back to the whole journey, steadily.
      timer = window.setTimeout(() => {
        fitRef.current(true, { duration: RETURN_DURATION_MS, easing: easeInOutSine });
      }, RETURN_DWELL_MS);
    }
    return () => window.clearTimeout(timer);
  }, [focusKey, previewKey]);

  const previewGeoJson = useMemo(() => {
    if (!previewPoint) return null;
    const at: [number, number] = [previewPoint.lng, previewPoint.lat];
    const features = [
      validPoint(previewPoint.from) ? curveSegment([previewPoint.from.lng, previewPoint.from.lat], at, CURVATURE) : null,
      validPoint(previewPoint.to) ? curveSegment(at, [previewPoint.to.lng, previewPoint.to.lat], CURVATURE) : null,
    ].filter((c): c is Array<[number, number]> => c != null).map((coordinates) => ({
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'LineString' as const, coordinates },
    }));
    return { type: 'FeatureCollection' as const, features };
  }, [previewPoint]);

  // Keep the candidate in view: only when it falls outside the current frame
  // does the map widen to the route plus the candidate (moving between cards
  // near the route then doesn't jolt the map), and it frames the route again
  // once the hover ends.
  const widenedRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!previewPoint) {
      if (widenedRef.current) {
        widenedRef.current = false;
        // The highlight camera takes over when a stop is still hovered or a
        // way home is pending; otherwise frame the route again here.
        if (!focusKey && !cameraAwayRef.current) fitToPoints(true);
      }
      return;
    }
    const bounds = map.getBounds();
    if (bounds && bounds.contains([previewPoint.lng, previewPoint.lat])) return;
    let minLng = previewPoint.lng, maxLng = previewPoint.lng, minLat = previewPoint.lat, maxLat = previewPoint.lat;
    for (const p of points) {
      minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
      minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
    }
    widenedRef.current = true;
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: { top: 84, bottom: 48, left: 48, right: 48 }, duration: 500, maxZoom: 9 });
    // previewKey stands for previewPoint (a new object on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, points, fitToPoints]);

  if (!MAPBOX_TOKEN) return <MissingTokenPlaceholder height={fill ? '100%' : height} />;
  if (points.length === 0) return null;

  const initialCenter: [number, number] = [points[0].lng, points[0].lat];

  return (
    <div
      style={{
        position: 'relative',
        height: fill ? '100%' : height,
        borderRadius: fill ? 0 : 8,
        overflow: 'hidden',
        border: fill ? 'none' : '1px solid #e2e8f0',
      }}
    >
      <Map
        ref={attachMap}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: initialCenter[0], latitude: initialCenter[1], zoom: 4 }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        style={{ width: '100%', height: '100%' }}
        onLoad={() => fitToPoints(false)}
        // Mapbox also reports clicks on markers (they sit in its canvas
        // container): only a click away from every stop counts here.
        onClick={onBackgroundClick ? (e) => {
          if ((e.originalEvent.target as HTMLElement | null)?.closest('.mapboxgl-marker')) return;
          onBackgroundClick();
        } : undefined}
      >
        {points.length > 1 && (
          <Source id="route-line" type="geojson" data={styledLines}>
            {/* White casing beneath the colored line for a crisp, elegant edge.
                Highlight (`state`): focus = wider, drawn on top; dim = faded. */}
            <Layer
              id="route-line-casing"
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': SORT_BY_STATE }}
              paint={{
                'line-color': '#ffffff',
                'line-width': ['match', ['get', 'state'], 'focus', 10, 7],
                'line-opacity': CASING_OPACITY_BY_STATE,
              }}
            />
            <Layer
              id="route-line-layer"
              type="line"
              filter={['!=', ['get', 'dashed'], true]}
              layout={{ 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': SORT_BY_STATE }}
              paint={{ 'line-color': ['get', 'color'], 'line-width': LINE_WIDTH_BY_STATE, 'line-opacity': LINE_OPACITY_BY_STATE }}
            />
            <Layer
              id="route-line-flight-layer"
              type="line"
              filter={['==', ['get', 'dashed'], true]}
              layout={{ 'line-cap': 'butt', 'line-join': 'round', 'line-sort-key': SORT_BY_STATE }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': LINE_WIDTH_BY_STATE,
                'line-opacity': LINE_OPACITY_BY_STATE,
                'line-dasharray': [2, 1.5],
              }}
            />
          </Source>
        )}

        {previewGeoJson && previewGeoJson.features.length > 0 && (
          <Source id="route-preview" type="geojson" data={previewGeoJson}>
            <Layer
              id="route-preview-casing"
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.85 }}
            />
            <Layer
              id="route-preview-line"
              type="line"
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{ 'line-color': PIN_COLOR, 'line-width': 2.5, 'line-opacity': 0.8, 'line-dasharray': [1.6, 1.4] }}
            />
          </Source>
        )}

        {markers.map((m) => {
          const state = markerState(m.lng, m.lat);
          return (
            // The focused stop's marker (and label) sits above its neighbours.
            <Marker key={m.id} longitude={m.lng} latitude={m.lat} anchor="center" style={{ zIndex: state === 'focus' ? 2 : state === 'normal' && highlightActive ? 1 : 0 }}>
              <StopMarker
                kind={m.kind}
                label={m.label}
                number={m.number}
                state={state}
                onHoverChange={onStopClick ? (hovered) => setMapHoverStopId((cur) => (hovered ? m.id : cur === m.id ? null : cur)) : undefined}
                onClick={onStopClick ? () => onStopClick(m.id) : undefined}
              />
            </Marker>
          );
        })}

        {previewPoint && (
          <Marker longitude={previewPoint.lng} latitude={previewPoint.lat} anchor="center" style={{ zIndex: 3 }}>
            <PreviewMarker label={previewPoint.label} />
          </Marker>
        )}
      </Map>
      <style>{PIN_DROP_CSS}</style>
      {showLegend && <Legend open={legendOpen} onToggle={toggleLegend} />}
    </div>
  );
};

export default JourneyRouteMap;
